"use client"
import { useEffect } from "react"
import Link from "next/link"
import clsx from "clsx"
import { linkUnderlineClass } from "@/data/styles"
import { HeaderIcon } from "./HeaderIcon"
import { CloseIcon } from "@/components/icons"
import { usePathname } from "next/navigation"

interface MobileMenuProps {
  menuOpen: boolean
  setMenuOpen: (open: boolean) => void
  links?: { label: string; href?: string }[]
  homeLinks?: { label: string; href?: string }[]
  icons?: { Icon: React.ComponentType<{ color?: string; className?: string }>; href: string }[]
}

export const MobileMenu = ({ menuOpen, setMenuOpen, links, homeLinks, icons }: MobileMenuProps) => {
  const pathname = usePathname()

  // Disable scrolling when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [menuOpen])

  // Close menu if window width reaches xl breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setMenuOpen(false)
      
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    
    return () => window.removeEventListener("resize", handleResize)
  }, [setMenuOpen])

  return (
    <nav
      className={clsx(
        `xl:hidden fixed top-0 left-0 w-full h-screen z-40 transition-transform duration-300 ease-in-out 
        bg-black/80 backdrop-blur-sm text-white`, menuOpen ? "translate-y-0" : "-translate-y-full"
      )}
    >
      <button
        aria-label="close-filters"
        onClick={() => setMenuOpen(false)}
        className="absolute top-6 right-6 text-3xl font-light p-2 hover:opacity-80 transition-opacity duration-300 cursor-pointer"
      >
        <CloseIcon color="white" width={25} height={25} />
      </button>

      <div className="flex flex-col items-center justify-center h-full portrait:gap-6 landscape:gap-3 px-[20px]">
        <div
          className="
            flex flex-col items-center justify-center gap-6 portrait:flex-col 
            landscape:flex-row landscape:flex-wrap landscape:gap-4
          "
        >
          {links?.map((link, index) => {
            const isActive = pathname === link.href
            return (
              <Link
                key={index}
                href={link?.href ?? "#"}
                className={clsx(
                  linkUnderlineClass,
                  "w-fit text-base sm:text-lg",
                  isActive && "after:w-full after:opacity-100"
                )}
              >
                {link.label}
              </Link>
            )
          })}
        </div>

        <div className="flex gap-4 flex-wrap justify-center mt-[10px]">
          {homeLinks?.map((link, index) => {
            const isActive = pathname === link.href
            return (
              <Link
                key={index}
                href={link?.href ?? "#"}
                className={clsx(
                  "px-4 py-2 rounded-full text-sm font-extralight transition-all duration-300",
                  isActive
                    ? "bg-white text-black shadow-md"
                    : "bg-white/10 text-white hover:bg-white/20"
                )}
              >
                {link.label}
              </Link>
            )
          })}
        </div>

        {icons && (
          <div className="flex justify-center gap-6 mt-4">
            {icons.map(({ Icon, href }, idx) => (
              <HeaderIcon key={idx} variant="transparent" Icon={Icon} href={href} />
            ))}
          </div>
        )}
      </div>
    </nav>
  )
}
